
import { useNavigate } from 'react-router-dom';
import type { CartItem } from '../../../utils/cart';

interface CartSummaryProps {
  items: CartItem[];
}

export default function CartSummary({ items }: CartSummaryProps) {
  const navigate = useNavigate();
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const freeShippingThreshold = 200;
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - subtotal);
  const freeShippingProgress = Math.min(100, (subtotal / freeShippingThreshold) * 100);

  return (
    <div className="bg-[#F9F6F2] rounded-2xl p-6 lg:sticky lg:top-24">
      {/* Header */}
      <h2 className="text-lg font-serif text-gray-900 mb-5 flex items-center gap-2">
        <i className="ri-file-list-3-line text-[#D4A574] text-lg w-5 h-5 flex items-center justify-center"></i>
        Résumé de la commande
      </h2>

      {/* Free Shipping Progress */}
      <div className="mb-6 p-3.5 bg-white rounded-xl">
        {remainingForFreeShipping > 0 ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-truck-line text-[#D4A574] text-sm w-4 h-4 flex items-center justify-center"></i>
              <p className="text-xs text-gray-600">
                Plus que <strong className="text-[#143D28]">${remainingForFreeShipping.toFixed(2)}</strong> pour la livraison gratuite
              </p>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#D4A574] to-[#C4956A] rounded-full transition-all duration-500"
                style={{ width: `${freeShippingProgress}%` }}
              ></div>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-full bg-[#143D28] flex items-center justify-center flex-shrink-0">
              <i className="ri-check-line text-white text-xs"></i>
            </div>
            <p className="text-xs text-[#143D28] font-medium">
              Livraison gratuite débloquée !
            </p>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="space-y-3 mb-5">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">
            Sous-total ({totalItems} article{totalItems > 1 ? 's' : ''})
          </span>
          <span className="font-medium text-gray-900">${subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Livraison</span>
          <span className="text-xs text-gray-400 italic">Calculé à l&apos;étape suivante</span>
        </div>
      </div>

      {/* Divider */}
      <div className="h-[1px] bg-[#D4A574]/15 mb-5"></div>

      {/* Total */}
      <div className="flex justify-between items-baseline mb-6">
        <span className="text-base font-serif text-gray-900">Total estimé</span>
        <span className="text-2xl font-serif font-bold text-[#143D28]">${subtotal.toFixed(2)}</span>
      </div>

      {/* CTA */}
      <button
        onClick={() => navigate('/payment')}
        className="w-full py-3.5 bg-[#143D28] text-white text-sm font-semibold rounded-full hover:bg-[#0F2E1E] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 group"
      >
        <span>PROCÉDER AU PAIEMENT</span>
        <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform duration-300"></i>
      </button>

      {/* Security */}
      <div className="mt-4 flex items-center justify-center gap-1.5 text-gray-400 text-[11px]">
        <i className="ri-shield-check-line text-sm w-4 h-4 flex items-center justify-center"></i>
        <span>Paiement 100% sécurisé</span>
      </div>

      {/* Trust Badges */}
      <div className="mt-5 pt-5 border-t border-[#D4A574]/10">
        <div className="grid grid-cols-1 gap-2.5">
          <div className="flex items-center gap-2.5 text-xs text-gray-500">
            <i className="ri-truck-line text-[#D4A574] text-sm w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
            <span>Livraison gratuite dès $200</span>
          </div>
          <div className="flex items-center gap-2.5 text-xs text-gray-500">
            <i className="ri-arrow-go-back-line text-[#D4A574] text-sm w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
            <span>Retours gratuits sous 30 jours</span>
          </div>
          <div className="flex items-center gap-2.5 text-xs text-gray-500">
            <i className="ri-customer-service-2-line text-[#D4A574] text-sm w-4 h-4 flex items-center justify-center flex-shrink-0"></i>
            <span>Support client 24/7</span>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="mt-5 pt-4 border-t border-[#D4A574]/10">
        <p className="text-[10px] text-gray-400 uppercase tracking-wider text-center mb-2.5">Moyens de paiement acceptés</p>
        <div className="flex items-center justify-center gap-3">
          <i className="ri-visa-line text-xl text-gray-300 w-6 h-6 flex items-center justify-center"></i>
          <i className="ri-mastercard-line text-xl text-gray-300 w-6 h-6 flex items-center justify-center"></i>
          <i className="ri-paypal-line text-xl text-gray-300 w-6 h-6 flex items-center justify-center"></i>
          <i className="ri-bank-card-line text-xl text-gray-300 w-6 h-6 flex items-center justify-center"></i>
        </div>
      </div>
    </div>
  );
}
